import { useCallback, useContext, useRef } from 'react';

import resumeCardCommentButtonClick from '@hh.ru/analytics-js-events/build/xhh/employer/responses/resume_card/resume_card_comment_button_click';
import { Button, TooltipHover } from '@hh.ru/magritte-ui';
import { PenSquareOutlinedSize16 } from '@hh.ru/magritte-ui/icon';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import { ResumeCardActionsContext } from 'src/components/ResumeCard/Actions/Context';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import { ResumeComments } from 'src/models/employer/resume/commentsByUser';

const TrlKeys = {
    buttonText: 'vacancyResponse.comments',
    hiringManagerTooltip: 'vacancyResponse.comments.hiringManagerTooltip',
};

interface Props {
    comments: ResumeComments;
    isMobile?: boolean;
}

const CommentsButton: TranslatedComponent<Props> = ({ comments, isMobile, trls }) => {
    const isHiringManager = useSelector((state) => state.isHiringManager);
    const activatorRef = useRef<HTMLButtonElement>(null);

    const {
        commentToggle,
        isNew,
        markAsRead,
        messagesToggle,
        vacancyId,
        lastEmployerStateExtName,
        resume,
        isResponse,
    } = useContext(ResumeCardActionsContext);
    const topicId = resume.topicId;

    const [, toggleComments] = commentToggle;
    const [, , setDialogue] = messagesToggle;

    const handleClick = useCallback(() => {
        if (topicId && lastEmployerStateExtName) {
            resumeCardCommentButtonClick({
                topicId,
                vacancyId,
                folderName: lastEmployerStateExtName,
                commentCount: comments?.total,
            });
        }
        isNew && markAsRead?.();
        toggleComments();
        setDialogue(false);
    }, [comments, isNew, lastEmployerStateExtName, markAsRead, setDialogue, toggleComments, topicId, vacancyId]);

    return (
        <>
            <Button
                stretched={isMobile}
                size="small"
                mode="secondary"
                icon={<PenSquareOutlinedSize16 />}
                postfix={comments?.total && (comments.total > 99 ? '99+' : comments.total)}
                onClick={handleClick}
                aria-label={trls[TrlKeys.buttonText]}
                data-qa="show-resume-comments"
                ref={activatorRef}
            >
                {(isMobile || !isResponse) && trls[TrlKeys.buttonText]}
            </Button>
            {isHiringManager && (
                <TooltipHover placement="bottom-center" activatorRef={activatorRef}>
                    {trls[TrlKeys.hiringManagerTooltip]}
                </TooltipHover>
            )}
        </>
    );
};

export default translation(CommentsButton);
